import { Typography } from '@material-ui/core';
import { Business, Phone } from '@material-ui/icons';
import { Stack } from '@superdispatch/ui';
import { Alert, DescriptionItem } from '@superdispatch/ui-lab';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import { PoundIcon } from 'shared/icons';
import { useCarrierInfo } from 'shared/modules/carrier-profile/data/CarrierInfoAPI';
import { useVerifiedCarrierApplicationStatus } from 'shared/modules/carrier-profile/data/VerifiedCarrierApplicationStatusAPI';
import { trackPhoneVerificationEvent } from 'shared/modules/phone-verification/data/PhoneVerificationAnalytics';
import { LinkButton } from 'shared/routing/Links';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { NullableDateString, parseDate } from '@superdispatch/dates';

function PhoneGracePeriod({
  gracePeriodEndsAt,
}: {
  gracePeriodEndsAt: NullableDateString;
}) {
  const date = parseDate(gracePeriodEndsAt);
  const formattedTime = date.toFormat('HH:mm');
  const formattedDate = date.toFormat('MM/dd');
  return (
    <Typography>
      Verify your new FMCSA-registered phone number before <br />{' '}
      {formattedTime} on {formattedDate} to keep your Verified Carrier status.
    </Typography>
  );
}

export function ProfileCarrierInfoDetails() {
  const { data: carrierInfo } = useCarrierInfo();
  const { data: verificationStatus } = useVerifiedCarrierApplicationStatus();
  const { data: settings } = useCarrierSettings();
  const isVerificationDisabled =
    !settings?.drivers_can_edit_carrier_info && MobileAppBridge.isInjected();

  const hasFmcsaPhoneRevokingReasons =
    !!verificationStatus?.verified_carrier_application.revoking_reasons
      ?.fmcsa_phone?.length;

  const {
    is_verified: isFMCSAPhoneVerified,
    phone_number: fmcsaPhone,
    grace_period_ends_at: gracePeriodEndsAt,
  } = carrierInfo?.fmcsa_phone_number || {};

  return (
    <Stack space="xsmall">
      <Typography variant="h4">Safer FMCSA Record</Typography>
      <DescriptionItem icon={<Business />}>{carrierInfo?.name}</DescriptionItem>
      <DescriptionItem icon={<PoundIcon />}>
        {carrierInfo?.usdot_number}{' '}
        <Typography variant="caption" color="textSecondary">
          · USDOT
        </Typography>
      </DescriptionItem>
      {isFMCSAPhoneVerified &&
      !gracePeriodEndsAt &&
      !hasFmcsaPhoneRevokingReasons ? (
        <DescriptionItem icon={<Phone />}>{fmcsaPhone}</DescriptionItem>
      ) : (
        <Alert
          severity={
            hasFmcsaPhoneRevokingReasons || gracePeriodEndsAt
              ? 'critical'
              : 'caution'
          }
        >
          <Stack space="xsmall">
            {gracePeriodEndsAt ? (
              <PhoneGracePeriod gracePeriodEndsAt={gracePeriodEndsAt} />
            ) : (
              'Verify your phone number and help us keep our platform secure and your identity safe.'
            )}
            <LinkButton
              variant="neutral"
              size="small"
              to="/profile/phone-verification"
              onClick={() => {
                trackPhoneVerificationEvent({
                  name: 'Carrier Clicked Verify Phone Number Now',
                  carrier_verification_status:
                    verificationStatus?.verified_carrier_application.status,
                });
              }}
              disabled={isVerificationDisabled}
            >
              Verify now
            </LinkButton>
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}
